import type { SharingClosedReason, SharingRequest, SharingRequestStatus } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { I18n } from '@/i18n/I18nProvider.tsx'

/**
 * `sharing` — the sharing-request queue's sentences (PRD-26 *The interface* → Governance →
 * Sharing requests).
 *
 * "Sharing request", "Send for review", "Waiting for review", "Approve and share", "Decline" —
 * never "curator", "grant" or "approval queue" (D-37).
 *
 * English is the source of truth AND the type (PRD-27 LOC-011). What decides WHICH of these
 * sentences a decision gets is in `components/sharing/sharing-outcome.ts`, because a rule that
 * could drift per locale is a bug rather than a translation (LOC-017, D-25).
 */

/**
 * What these sentences need from the reader's language (PRD-27 LOC-016, LOC-020).
 *
 * A parameter rather than an import: a catalogue is data, and one that reached for the locale
 * would capture it at import and go on saying the previous language after a switch, with
 * nothing failing (F-3).
 */
export type SharingFmt = Pick<I18n, 'locale' | 'formatCount' | 'formatList'>

/**
 * Which of an entry's own plural forms to say. Never `count === 1` — Brazilian Portuguese puts
 * ZERO in the `one` category, so a two-branch check is wrong in one shipped language already.
 */
const pick = (fmt: SharingFmt, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

const counted = (fmt: SharingFmt, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pick(fmt, count, forms)}`

const people = (fmt: SharingFmt, count: number): string => counted(fmt, count, { one: 'person', other: 'people' })

export const sharing = {
  view: 'Sharing requests',
  /** Said after a view's name to a screen reader: "Sharing requests, 3 waiting", not a bare "3". */
  waiting: (fmt: SharingFmt, count: number): string => `${fmt.formatCount(count)} waiting`,
  lead: 'Agents and knowledge that someone wants to share beyond the groups they manage. Nothing here is live until you approve it.',
  empty: 'Nothing is waiting for review',
  emptyBody:
    'When someone shares an agent or a knowledge base with everyone, or with a group they do not manage, it waits here. Sharing with groups they manage goes live without you.',
  loading: 'Loading sharing requests…',
  failed: 'Sharing requests could not be loaded.',
  failedBody: 'This is a problem reading the queue, not a queue with nothing in it. Try again in a moment.',
  showHistory: 'Show decided requests',
  hideHistory: 'Show only what is waiting',
  kinds: { agent: 'Agent', knowledge_base: 'Knowledge base' } satisfies Record<SharingRequest['resourceKind'], string>,
  asked: (name: string | null, when: string): string => `Asked by ${name ?? 'someone who has since left'} · ${when}`,
  wantsToShare: 'Wants to share it with',
  everyone: 'Everyone who signs in',
  added: 'added',
  now: 'Who can use it now',
  nowPrivate: 'Only its maintainers',
  nowEveryone: 'Everyone who signs in',
  reach: (fmt: SharingFmt, now: number, ifApproved: number): string =>
    `Reaches ${people(fmt, now)} now; ${people(fmt, ifApproved)} if you approve it.`,
  why: {
    everyone: 'Sharing with everyone always needs an administrator’s review.',
    unmanaged_groups: (fmt: SharingFmt, requester: string | null, groups: string[]): string =>
      `${requester ?? 'The person who asked'} does not manage ${fmt.formatList(groups)}.`
  },
  /** The same people mid-sentence, where `why` has them as the subject (PRD-27 LOC-014). */
  personWhoAsked: 'the person who asked',
  what: 'What they would get',
  version: (wouldPublish: number, requested: number, live: number | null): string =>
    wouldPublish === requested
      ? `Version ${wouldPublish} would go live${live === null ? '' : live === wouldPublish ? ' — it is already live for who can use it now' : ` (version ${live} is live now)`}.`
      : `Version ${wouldPublish} would go live — they asked about version ${requested}, and a newer one has been published since.`,
  changedSince: (fmt: SharingFmt, fields: string[]): string => `Changed since they asked: ${fmt.formatList(fields)}.`,
  fieldNames: {
    instructions: 'instructions',
    knowledge: 'knowledge',
    capabilities: 'what it can do',
    grounded: 'grounding',
    memory: 'memory',
    displayName: 'name',
    description: 'description',
    tagline: 'summary',
    starters: 'starters',
    category: 'category',
    icon: 'icon',
    accent: 'colour',
    knowledgeMode: 'knowledge'
  } as Record<string, string>,
  /**
   * A field this client has no word for. `changedSince[].field` is `z.string()` on the wire, so
   * an unmapped one used to be rendered as its own identifier — "systemPromptPrefix" in the
   * middle of a sentence an administrator is deciding on (PRD-27 LOC-018). Saying that something
   * changed without naming it is less informative and more honest; the list beside it still
   * names everything this client does know.
   */
  fieldUnknown: 'something else',
  knowledge: 'Knowledge it answers from',
  knowledgeAll: 'Everything each person can use — no bases are attached.',
  abilities: 'What it can do',
  noAbilities: 'It only answers; it cannot act.',
  reads: 'reads',
  acts: 'acts',
  grounded: 'Answers only from its knowledge.',
  notGrounded: 'Can answer from general knowledge as well as its own.',
  memoryLearns: 'Remembers what people ask it to.',
  memoryDoesNot: 'Does not learn anything new about people.',
  showInstructions: 'Show its instructions',
  hideInstructions: 'Hide its instructions',
  base: (fmt: SharingFmt, documents: number, folders: number): string =>
    `${counted(fmt, documents, { one: 'document', other: 'documents' })}${
      folders === 0 ? '' : ` · ${counted(fmt, folders, { one: 'connected folder', other: 'connected folders' })}`
    }`,
  sampleTitles: 'Some of what it holds',
  coversLater: {
    agent: 'After approval, later versions reach these people without another review.',
    knowledge_base: 'After approval, everything added to it later reaches these people too.'
  } satisfies Record<SharingRequest['resourceKind'], string>,
  acknowledged: 'What they confirmed when they asked',
  note: 'Note to the person who asked',
  noteHint: 'Required to decline — say why, so they can act on it.',
  approve: 'Approve and share',
  decline: 'Decline',
  deciding: 'Saving…',
  stale: 'This request changed while you were reading it. It has been reloaded — read it again before you decide.',
  alreadyDecided: 'This request was decided a moment ago. The list has been reloaded.',
  /** Every one of these describes a REQUEST, which is feminine in Portuguese and uniform here. */
  status: {
    pending: 'Waiting for review',
    approved: 'Approved',
    declined: 'Declined',
    withdrawn: 'Withdrawn',
    superseded: 'Replaced by a newer request',
    closed: 'Closed'
  } satisfies Record<SharingRequestStatus, string>,
  closedReason: {
    group_deleted: 'a group it named was deleted',
    requester_lost_authority: 'the person who asked no longer maintains it',
    item_unavailable: 'it can no longer be shared as asked',
    migrated: 'the groups it named no longer exist'
  } satisfies Record<SharingClosedReason, string>,
  decidedBy: (name: string | null, when: string): string => `${name ?? 'Someone'} · ${when}`,
  /** The request a notification named, when it is no longer waiting. */
  notWaiting: (request: Pick<SharingRequest, 'status' | 'resourceName' | 'decidedBy'>): string =>
    request.status === 'superseded'
      ? `The request for “${request.resourceName}” was replaced by a newer one — it is in the list below if it still waits.`
      : request.status === 'withdrawn'
        ? `The request for “${request.resourceName}” was withdrawn, so there is nothing to decide.`
        : request.status === 'pending'
          ? `The request for “${request.resourceName}” is waiting.`
          : `The request for “${request.resourceName}” was already ${request.status === 'approved' ? 'approved' : request.status === 'declined' ? 'declined' : 'closed'}${request.decidedBy ? ` by ${request.decidedBy.displayName}` : ''}.`,
  open: {
    agent: 'Open the agent',
    knowledge_base: 'Open the knowledge base'
  } satisfies Record<SharingRequest['resourceKind'], string>,

  /**
   * What a decision did, said after the card leaves the queue (GRP-080).
   *
   * The server may close a request instead of approving it — the person who asked no longer
   * maintains the item, it was paused, every group it named was deleted — and a card that
   * simply vanished would read as shared. Which of these is said is
   * `components/sharing/sharing-outcome.ts`'s decision; the words are this file's.
   */
  outcome: {
    approvedEveryone: (name: string): string => `Approved — everyone who signs in can use “${name}” now.`,
    approvedGroups: (fmt: SharingFmt, groups: string[], name: string): string =>
      `Approved — ${fmt.formatList(groups)} can use “${name}” now.`,
    declined: (who: string): string => `Declined — ${who} is told, with your note.`,
    closed: (name: string, why: string): string =>
      `Nothing was shared: the request for “${name}” was closed, because ${why}.`,
    closedUnknown: 'it could not be shared as asked',
    /** Anything else the server settled on: its own status word, then what it was about. */
    other: (status: string, name: string): string => `${status} — “${name}”.`
  }
}
